import type { MeterkastData } from '@shared/types';

// The API (AnalyseResultaat) uses different field names than the original AIAnalyse type.
// This component accepts both shapes defensively to prevent crashes.
interface AnalyseInput {
  // AIAnalyse format (legacy/typed)
  aansluiting?: string;
  hoofdzekering?: string;
  aantal_groepen?: number;
  aardlekschakelaar?: boolean;
  betrouwbaarheid?: {
    aansluiting?: number;
    hoofdzekering?: number;
    aantal_groepen?: number;
    aardlekschakelaar?: number;
  };
  // AnalyseResultaat format (actual API response)
  aansluitingType?: string;
  hoofdzekeringsAmpere?: number;
  aantalGroepen?: number;
  heeftAardlekschakelaar?: boolean;
  vertrouwensniveau?: 'hoog' | 'gemiddeld' | 'laag';
  _stub?: boolean;
}

interface Props {
  analyse: AnalyseInput;
  meterkast: MeterkastData;
  onMeterkastChange: (data: MeterkastData) => void;
}

function BetrouwbaarheidsLabel({ pct }: { pct: number | undefined }) {
  if (pct === undefined || pct === null) {
    return <span className="lc-betrouwbaarheid lc-betrouwbaarheid--onbekend">—</span>;
  }
  if (pct >= 85) return <span className="lc-betrouwbaarheid lc-betrouwbaarheid--hoog">✅ {pct}%</span>;
  if (pct >= 65) return <span className="lc-betrouwbaarheid lc-betrouwbaarheid--middel">⚠️ {pct}%</span>;
  return <span className="lc-betrouwbaarheid lc-betrouwbaarheid--laag">❓ {pct}%</span>;
}

export default function AIResultaat({ analyse, meterkast, onMeterkastChange }: Props) {
  // Null-guard: betrouwbaarheid may be absent when API returns vertrouwensniveau instead
  const b = analyse.betrouwbaarheid ?? {};

  // Handle both field name formats (typed AIAnalyse vs raw API AnalyseResultaat)
  const aansluitingWaarde = analyse.aansluiting ?? analyse.aansluitingType ?? '—';
  const hoofdzekeringsWaarde =
    analyse.hoofdzekering ??
    (analyse.hoofdzekeringsAmpere != null ? `${analyse.hoofdzekeringsAmpere}A` : '—');
  const aantalGroepenWaarde = analyse.aantal_groepen ?? analyse.aantalGroepen;
  const aardlekWaarde = analyse.aardlekschakelaar ?? analyse.heeftAardlekschakelaar;

  const rijen = [
    {
      veld: 'Type aansluiting',
      waarde: aansluitingWaarde,
      betrouwbaarheid: b.aansluiting,
    },
    {
      veld: 'Hoofdzekering',
      waarde: hoofdzekeringsWaarde,
      betrouwbaarheid: b.hoofdzekering,
    },
    {
      veld: 'Aantal groepen',
      waarde: aantalGroepenWaarde != null ? String(aantalGroepenWaarde) : '—',
      betrouwbaarheid: b.aantal_groepen,
    },
    {
      veld: 'Aardlekschakelaar',
      waarde: aardlekWaarde != null ? (aardlekWaarde ? 'Ja' : 'Nee') : '—',
      betrouwbaarheid: b.aardlekschakelaar,
    },
  ];

  return (
    <div className="lc-ai-resultaat">
      <h3 className="lc-ai-resultaat__titel">AI-herkenning resultaat</h3>
      <p className="lc-ai-resultaat__beschrijving">
        We hebben het volgende herkend op uw foto. Controleer of dit klopt.
      </p>

      {analyse._stub && (
        <p className="lc-badge lc-badge--oranje">Demo-modus: dit zijn testwaarden</p>
      )}

      <table className="lc-ai-resultaat__tabel">
        <thead>
          <tr>
            <th>Gegeven</th>
            <th>Herkend</th>
            <th>Zekerheid</th>
          </tr>
        </thead>
        <tbody>
          {rijen.map((rij) => (
            <tr key={rij.veld}>
              <td>{rij.veld}</td>
              <td>{rij.waarde}</td>
              <td><BetrouwbaarheidsLabel pct={rij.betrouwbaarheid} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="lc-ai-resultaat__acties">
        <p className="lc-ai-resultaat__vraag">Klopt dit?</p>
        <button
          type="button"
          className="lc-knop lc-knop--primair lc-knop--klein"
          onClick={() => {
            // Accept AI results: pre-fill meterkast with AI data
            const aansluiting =
              aansluitingWaarde === '3-fase' ? '3-fase' :
              aansluitingWaarde === '1-fase' ? '1-fase' :
              null;
            onMeterkastChange({
              ...meterkast,
              aansluiting: aansluiting as MeterkastData['aansluiting'],
              hoofdzekering: (analyse.hoofdzekering as MeterkastData['hoofdzekering']) ?? null,
              aantalGroepen: aantalGroepenWaarde ?? meterkast.aantalGroepen,
              heeftAardlek: aardlekWaarde ?? meterkast.heeftAardlek,
            });
          }}
        >
          Ja, dit klopt
        </button>
        <button
          type="button"
          className="lc-knop lc-knop--secondair lc-knop--klein"
          onClick={() => {
            // User wants to manually correct — show StapHandmatig inline
            // The parent will handle routing to handmatige invoer
          }}
        >
          Nee, ik pas aan
        </button>
      </div>
    </div>
  );
}
